import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import {
  isWarmTransferInProgressStage,
  type WarmTransferStage,
} from '@/constants/warmTransfer';
import type { RootState } from '@/store';
import type {
  WarmTransferActiveContext,
  WarmTransferHandoffContext,
} from '@/types/warmTransfer';

interface WarmTransferState {
  isModalOpen: boolean;
  modalView: 'selectTarget' | 'inProgress';
  stage: WarmTransferStage;
  initialContactId: string | null;
  targetType: 'spog_agent' | 'teams_user' | 'queue' | 'teams_group' | null;
  targetId: string | null;
  targetDisplayName: string | null;
  targetPhoneNumber: string | null;
  requestId: string | null;
  answeringDisplayName: string | null;
  /** Agent 2 cached context after accept (store deleted on backend). */
  cachedConsultContext: WarmTransferHandoffContext | null;
  pendingAcceptedWarmConsult: {
    initialContactId: string;
    requestId: string;
    fromConnectId: string;
    fromDisplayName?: string | null;
    context?: WarmTransferHandoffContext | null;
  } | null;
  isConsultCall: boolean;
  /** Agent 2 is on the consult leg (not just queue routing). */
  consultAgentJoined: boolean;
}

const initialState: WarmTransferState = {
  isModalOpen: false,
  modalView: 'selectTarget',
  stage: 'idle',
  initialContactId: null,
  targetType: null,
  targetId: null,
  targetDisplayName: null,
  targetPhoneNumber: null,
  requestId: null,
  answeringDisplayName: null,
  cachedConsultContext: null,
  pendingAcceptedWarmConsult: null,
  isConsultCall: false,
  consultAgentJoined: false,
};

const warmTransferSlice = createSlice({
  name: 'warmTransfer',
  initialState,
  reducers: {
    openWarmTransferModal: state => {
      state.isModalOpen = true;
      state.modalView = 'selectTarget';
      state.stage = 'selectTarget';
    },
    closeWarmTransferModal: state => {
      state.isModalOpen = false;
      state.modalView = 'selectTarget';
      state.stage = 'idle';
    },
    setWarmTransferStage: (state, action: PayloadAction<WarmTransferStage>) => {
      state.stage = action.payload;
    },
    setWarmTransferModalView: (
      state,
      action: PayloadAction<'selectTarget' | 'inProgress'>
    ) => {
      state.modalView = action.payload;
    },
    setWarmTransferSession: (
      state,
      action: PayloadAction<{
        initialContactId: string;
        targetType: 'spog_agent' | 'teams_user' | 'queue' | 'teams_group';
        targetId: string;
        targetDisplayName: string;
        targetPhoneNumber?: string | null;
        requestId?: string | null;
      }>
    ) => {
      Object.assign(state, action.payload);
    },
    setWarmTransferAnsweringName: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.answeringDisplayName = action.payload;
    },
    setWarmTransferRequestId: (state, action: PayloadAction<string | null>) => {
      state.requestId = action.payload;
    },
    setCachedConsultContext: (
      state,
      action: PayloadAction<WarmTransferHandoffContext | null>
    ) => {
      state.cachedConsultContext = action.payload;
    },
    setPendingAcceptedWarmConsult: (
      state,
      action: PayloadAction<WarmTransferState['pendingAcceptedWarmConsult']>
    ) => {
      state.pendingAcceptedWarmConsult = action.payload;
    },
    clearPendingAcceptedWarmConsult: state => {
      state.pendingAcceptedWarmConsult = null;
    },
    setIsConsultCall: (state, action: PayloadAction<boolean>) => {
      state.isConsultCall = action.payload;
    },
    setConsultAgentJoined: (state, action: PayloadAction<boolean>) => {
      state.consultAgentJoined = action.payload;
    },
    resetWarmTransfer: () => initialState,
  },
});

export const {
  openWarmTransferModal,
  closeWarmTransferModal,
  setWarmTransferStage,
  setWarmTransferModalView,
  setWarmTransferSession,
  setWarmTransferAnsweringName,
  setWarmTransferRequestId,
  setCachedConsultContext,
  setPendingAcceptedWarmConsult,
  clearPendingAcceptedWarmConsult,
  setIsConsultCall,
  setConsultAgentJoined,
  resetWarmTransfer,
} = warmTransferSlice.actions;

export default warmTransferSlice.reducer;

export const selectIsWarmInProgress = (state: RootState): boolean =>
  isWarmTransferInProgressStage(state.warmTransfer.stage);

export type { WarmTransferActiveContext };
